// 감사 로그 쪽 이동 — 총 건수와 쪽 번호를 함께 보여준다.
//
// 예전에는 '이전 / n 페이지 / 다음' 만 있었다. 그러면 **끝이 어디인지 모른 채** 다음을
// 계속 누르게 된다. 총 건수가 있으면 "볼 만한 양인가"를 먼저 판단할 수 있다.
//
// ⚠️ 총 건수는 서버가 상한(10,000)까지만 센다 — 무한 COUNT 를 피하기 위한 값이다
//    (AuditQueryService.TOTAL_CAP). 상한을 넘으면 totalIsExact 가 false 로 오고,
//    여기서 "10,000건 이상" 으로 적는다. **모르면서 아는 척하지 않는다.**
import { useId } from "react";

const SIZES = [30, 50, 100] as const;

export function AuditPager({
  page,
  size,
  total,
  totalIsExact,
  hasNext,
  loading,
  onPage,
  onSize,
}: {
  page: number;
  size: number;
  total: number;
  totalIsExact: boolean;
  hasNext: boolean;
  loading?: boolean;
  onPage: (next: number) => void;
  onSize?: (next: number) => void;
}) {
  const sizeId = useId();
  const n = total.toLocaleString("ko-KR");

  // 상한을 넘긴 경우 쪽 수도 "이상" 이다 — 정확한 마지막 쪽을 모른다.
  const lastPage = Math.max(1, Math.ceil(total / Math.max(1, size)));
  const atLast = !hasNext;

  return (
    <div className="pager pager--audit">
      <p className="pager__summary" aria-live="polite">
        {total === 0 ? (
          "기록 없음"
        ) : (
          <>
            전체 <strong>{n}건</strong>
            {!totalIsExact && " 이상"}
            {" · "}
            {page + 1} / {totalIsExact ? lastPage : `${lastPage}+`} 쪽
          </>
        )}
      </p>

      <div className="pager__nav">
        <button type="button" className="btn-secondary" disabled={page === 0 || loading} onClick={() => onPage(0)}>
          « 처음
        </button>
        <button
          type="button"
          className="btn-secondary"
          disabled={page === 0 || loading}
          onClick={() => onPage(Math.max(0, page - 1))}
        >
          이전
        </button>
        <button type="button" className="btn-secondary" disabled={atLast || loading} onClick={() => onPage(page + 1)}>
          다음
        </button>
        {/* ⚠️ '마지막' 은 총 건수가 **정확할 때만** 보여준다. 상한을 넘긴 상태에서
            마지막 쪽으로 보내면 실제 마지막이 아닌 곳에 떨어진다. */}
        {totalIsExact && (
          <button
            type="button"
            className="btn-secondary"
            disabled={atLast || loading}
            onClick={() => onPage(lastPage - 1)}
          >
            마지막 »
          </button>
        )}
      </div>

      {onSize && (
        <div className="pager__size">
          <label htmlFor={sizeId}>쪽 크기</label>
          <select
            id={sizeId}
            value={size}
            disabled={loading}
            onChange={(e) => onSize(Number(e.target.value))}
          >
            {SIZES.map((s) => (
              <option key={s} value={s}>
                {s}건
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
